import { useRouteMatch } from 'react-router-dom';

import { DEMO_CONNECTION_INDEX } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  SavedConnectionsQuery,
  useSavedConnectionsQuery,
} from 'src/ui/graph/types';
import { useGetLastConnectionIndex } from 'src/ui/hooks/connection';
import { buildRoute } from 'src/ui/utils/buildRoute';

export const useOrgDomain = () => {
  const routeMatch = useRouteMatch<{ org: string }>({
    path: ['/o/:org([a-z0-9-]+)'],
  });

  return routeMatch?.params.org || '';
};

export const useCurrentConnectionIdx = (): string => {
  const routeMatch = useRouteMatch<{ org?: string; id?: string }>({
    path: ['/u/:id(\\d+|demo)', '/o/:org([a-z0-9-]+)/:id(\\d+)'],
  });

  // integer connection idx is valid
  if (routeMatch?.params.id && !Number.isNaN(parseInt(routeMatch.params.id))) {
    return routeMatch.params.id;
  }

  // demo connection is valid
  if (routeMatch?.params.id === 'demo') {
    return DEMO_CONNECTION_INDEX.toString();
  }

  // invalid connection idx
  return '';
};

/**
 * Picks the connection index to navigate to: the last visited one when it is
 * still available, otherwise the first available connection, otherwise an
 * empty string when the org has no connections at all.
 */
export const resolveConnectionIdx = (
  lastConnectionIndex: number | null,
  connections: SavedConnectionsQuery['listConnections']
): string => {
  if (
    lastConnectionIndex !== null &&
    (lastConnectionIndex === DEMO_CONNECTION_INDEX ||
      connections?.some((c) => c?.index === lastConnectionIndex))
  ) {
    // use the last connection index only when it exists
    return lastConnectionIndex.toString();
  }

  const firstIndex = connections?.[0]?.index;
  if (firstIndex !== undefined) {
    // fall back to the first available connection when nothing else is selected
    return firstIndex.toString();
  }

  return '';
};

export const useLandingConnectionIdx = (): string => {
  const org = useOrgDomain();
  const connectionIdx = useCurrentConnectionIdx();
  const lastConnectionIndex = useGetLastConnectionIndex(org);
  const { data } = useSavedConnectionsQuery(
    graphQLClient,
    org ? { org_domain: org } : {},
    {
      enabled:
        !connectionIdx && !org && lastConnectionIndex !== DEMO_CONNECTION_INDEX,
    }
  );

  if (connectionIdx) {
    return connectionIdx;
  }

  if (org) {
    // no root landing page without a connection id inside an org
    return '';
  }

  return resolveConnectionIdx(lastConnectionIndex, data?.listConnections);
};

const useConnectionIdxForOrg = (
  connectionIdx: string,
  org: string,
  enabled = true
): { connectionIdx: string; isLoading: boolean } => {
  const lastConnectionIndex = useGetLastConnectionIndex(org);
  // the demo connection lives only in the personal workspace; org routes never
  // resolve to it, so a demo index stored under an org key (stale/corrupt) is
  // ignored and we fall back to a real endpoint or the org dashboard.
  const personalDemoShortcut =
    !org && lastConnectionIndex === DEMO_CONNECTION_INDEX;
  const { data, isLoading, isFetching } = useSavedConnectionsQuery(
    graphQLClient,
    org ? { org_domain: org } : {},
    {
      enabled: enabled && !connectionIdx && !personalDemoShortcut,
    }
  );

  const resolve = (): string => {
    if (connectionIdx) {
      return connectionIdx;
    }

    if (
      lastConnectionIndex !== null &&
      (personalDemoShortcut ||
        data?.listConnections?.some((c) => c?.index === lastConnectionIndex))
    ) {
      // use the last connection index only when it exists
      // allowed for both orgs and non-orgs
      return lastConnectionIndex.toString();
    }

    if (data?.listConnections?.length) {
      // fall back to the first available connection when nothing else is selected
      // allowed for both orgs and non-orgs
      const firstIndex = data.listConnections[0]?.index;
      if (firstIndex !== undefined) {
        return firstIndex.toString();
      }
    }

    return '';
  };

  return { connectionIdx: resolve(), isLoading: isLoading || isFetching };
};

export const useConnectionIdxForNavigation = (
  connectionIdx: string
): string => {
  const org = useOrgDomain();
  return useConnectionIdxForOrg(connectionIdx, org).connectionIdx;
};

/**
 * Resolves the landing route for an arbitrary workspace: the org's (or the
 * personal workspace's) last-used endpoint, falling back to its first endpoint,
 * or the workspace root (org dashboard for an org) when it has no endpoints.
 * `enabled` gates the underlying connections query for callers that only know
 * the target org after other data settles.
 */
export const useResolveWorkspaceLandingRoute = (
  org: string,
  enabled = true
): { route: string; isLoading: boolean } => {
  const { connectionIdx, isLoading } = useConnectionIdxForOrg('', org, enabled);
  return { route: buildRoute(org, connectionIdx), isLoading };
};
